/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect} from 'react'
import {
  Row,
  Col,
  Pagination,
  Form,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import {ListHSCategoriesRow} from '../core/_models'
import {Link, useNavigate} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import {showSwal} from '../../../../../ui-kit'
import {deleteHSCategory} from '../core/_requests'
import {axiosPrivate} from '../../../../libs/axios'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListHSCategoriesTableProps = {
  rows?: ListHSCategoriesRow[] | null
  isLoading: boolean
  onAgreementDelete: (deletedId: number | string) => void
  subject: string
}

const ListHSCategoriesTable: FC<ListHSCategoriesTableProps> = ({
  rows,
  isLoading,
  onAgreementDelete,
  subject,
}) => {
  const [internalLoading, setInternalLoading] = useState(false)
  const [listAgreement, setListHSCategories] = useState<ListHSCategoriesRow[]>([])

  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})

  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const ability = useAbility(AbilityContext)

  const columnHelper = createColumnHelper<ListHSCategoriesRow>()

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => {
        const value = info.row.original
        return (
          <div className='hs-categories-name'>
            {info.getValue()} <span>{value.name_fr}</span>
          </div>
        )
      },
      meta: {
        headerClass: 'ps-4 text-start w-350px',
        cellClass: 'text-start w-350px',
      },
    }),
    columnHelper.accessor('category_type', {
      header: 'Type',
      cell: (info) => {
        return info.getValue()
      },
      meta: {
        headerClass: 'ps-4 text-start w-50px',
        cellClass: 'text-start w-50px',
      },
    }),
    (ability.can('update', subject) || ability.can('delete', subject)) &&
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => {
          return (
            <div className='d-flex gap-3 justify-content-center'>
              {ability.can('update', subject) ? (
                <Link to={`/master-data-hs-categories/${info.getValue()}`} className='link-primary'>
                  <span className='fa fa-eye'></span>
                </Link>
              ) : (
                ''
              )}
              {ability.can('delete', subject) ? (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    listAgreement.filter((item) => item.parent_id === info.getValue()).length >
                    0 ? (
                      <Tooltip id='edit-disabled-tooltip'>
                        Cannot delete while there are HS Category referring to this HS Category
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                >
                  <span className='d-inline-block'>
                    <Link
                      to='#'
                      className='link-danger'
                      onClick={() => handleDelete(info.getValue())}
                      style={{
                        pointerEvents:
                          listAgreement.filter((item) => item.parent_id === info.getValue())
                            .length > 0
                            ? 'none'
                            : 'auto',
                        backgroundColor: 'transparent',
                        borderColor: 'transparent',
                      }}
                    >
                      <span className='fa fa-trash'></span>
                    </Link>
                  </span>
                </OverlayTrigger>
              ) : (
                ''
              )}
            </div>
          )
        },
        meta: {
          headerClass: '',
          cellClass: 'text-center w-50px',
        },
        enableSorting: false,
      }),
  ].filter(Boolean) as ColumnDef<ListHSCategoriesRow, any>[]

  const handleDelete = (id) => {
    showSwal({ title: 'Are you sure you want to delete this HS Category?', icon: 'question' }).then((result) => {
      if (!result.isConfirmed) return

      deleteHSCategory(axiosPrivate, id).then((res) => {
        if (res.status === 200) {
          showSwal({
            title: 'HS Category deleted',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            onAgreementDelete(id)
          })
        }
      })
    })
  }

  // Cleanup function to reset the state when the component is unmounted
  useEffect(() => {
    setListHSCategories([])
    setInternalLoading(true)
    return () => {
      setListHSCategories([])
      setInternalLoading(false)
    }
  }, [])

  useEffect(() => {
    setInternalLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    setListHSCategories(rows!)
    setInternalLoading(false)
  }, [rows])

  const dataTable = useReactTable({
    data: listAgreement || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil((listAgreement?.length || 0) / pagination.pageSize),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav) => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 1,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1

  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length

  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div
              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                    ? 'Sort descending'
                    : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && (
                <span className='ms-2'>
                  {{
                    asc: <i className='fa-solid fa-sort-up'></i>,
                    desc: <i className='fa-solid fa-sort-down'></i>,
                    false: <i className='fa-solid fa-sort'></i>,
                  }[header.column.getIsSorted() as string] ?? <i className='fa-solid fa-sort'></i>}
                </span>
              )}
            </div>
          </th>
        )
      })}
    </tr>
  ))
  const displayBody =
    dataTable.getRowModel().rows.length > 0 ? (
      dataTable.getRowModel().rows.map((row) => (
        <tr key={row.id} className='align-middle'>
          {row.getVisibleCells().map((cell) => {
            return (
              <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            )
          })}
        </tr>
      ))
    ) : (
      <tr className='align-middle'>
        <td colSpan={99} className='text-center'>
          No Data
        </td>
      </tr>
    )

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {!internalLoading ? (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              <i className='fa-solid fa-circle text-success fs-9 me-2'></i>
              Showing {dataTable.getRowModel().rows.length > 0 ? firstIndexPerPages : 0} to{' '}
              {dataTable.getRowModel().rows.length > 0 ? lastIndexPerPages : 0} of{' '}
              {listAgreement?.length || 0} results
            </Col>
            <Col lg={3} md={3} className='action-table'>
              <Form.Group className='form-group' controlId=''>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({
                        pageIndex: 0,
                        pageSize: 10,
                      })
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search by Name'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_country_tradeflow'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              list country
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => handleNavigationPageChange('prev')}
                  disabled={pagination.pageIndex === 0}
                />
                {/* Start Dynamic Page */}
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          pageIndex: page - 1, // Convert 1-based page to 0-based index
                        })
                      }}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </div>
  )
}

export {ListHSCategoriesTable}
